import React, { createContext, ReactNode, useContext, useMemo } from "react";

import { IAppRoute } from "@App/types";
import { useLocation } from "@Libs/Router/hooks/useLocation";

import { serviceRequestPathFragment } from "../../config/routes";
import { IServiceRequestDraft, ITaskCreationMode } from "../types";
import { getDraftToken, setDraftStorageWritable } from "./useDraftStorage";

export interface IDraftIdentity {
  mode: ITaskCreationMode;
  isEditing: boolean;
  /**
   * The react-query cache key for the request being filled in: the task id in
   * edit mode, the draft's `signed_id` from storage in create mode.
   */
  draftKey: string | null;
  /** The published task being edited. Absent in create mode. */
  taskId?: number;
  /** The task Rails serialized into the page. Absent in create mode. */
  initialDraft?: IServiceRequestDraft;
  /**
   * The route fragment one screen of the flow matches, for `useParams`: the
   * flow context and the shell sit *above* the routes, so react-router hands
   * them no params and `useParams` has only this pattern to match the URL
   * with — the create one never matches an edit URL.
   */
  screenFragment: IAppRoute;
  /**
   * Which route one screen of the flow lives at, spread straight into
   * `navigate`: the edit path carries the task id, the create path does not.
   */
  screenRoute: (
    screenKey: string
  ) => { fragments: IAppRoute; params: Record<string, string> };
}

const DraftIdentityContext = createContext<IDraftIdentity | undefined>(
  undefined
);

export interface IDraftIdentityProviderProps {
  /** Rails props, snake_case as `react_helper` emits them. */
  serviceRequest?: IServiceRequestDraft;
  taskId?: number;
  children: ReactNode;
}

/**
 * Who this flow is filling in, for every screen below it.
 *
 * The mode is derived from the Rails props rather than from which route
 * matched, because this sits *above* the routes: the flow context and the
 * shell both need the identity, and neither can ask the router which screen it
 * is about to render.
 *
 * It also latches the draft storage read-only for the rest of the document:
 * edit mode walks the same screens as create, and any one of them writing the
 * token, the resume pointer or a dismissed chip would destroy an unrelated
 * create draft the same visitor had in progress.
 */
export const DraftIdentityProvider = ({
  serviceRequest,
  taskId,
  children,
}: IDraftIdentityProviderProps) => {
  const isEditing = Boolean(taskId);

  // In render rather than in an effect: the screens below write to storage on
  // their very first render, so a latch set afterwards would be set too late.
  setDraftStorageWritable(!isEditing);

  // Re-read on every navigation: the flow saves or clears the token and then
  // moves screen without a reload, so a value read once would go stale.
  useLocation();
  const draftKey = isEditing ? String(taskId) : getDraftToken();

  const value = useMemo<IDraftIdentity>(
    () => ({
      mode: isEditing ? "edit" : "create",
      isEditing,
      draftKey,
      taskId,
      initialDraft: serviceRequest,
      screenFragment: isEditing
        ? serviceRequestPathFragment.SERVICE_REQUEST_EDIT_SCREEN
        : serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN,
      screenRoute: (screenKey) =>
        isEditing
          ? {
              fragments:
                serviceRequestPathFragment.SERVICE_REQUEST_EDIT_SCREEN,
              params: { id: String(taskId), screenKey },
            }
          : {
              fragments:
                serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN,
              params: { screenKey },
            },
    }),
    [serviceRequest, taskId, isEditing, draftKey]
  );

  return (
    <DraftIdentityContext.Provider value={value}>
      {children}
    </DraftIdentityContext.Provider>
  );
};

/**
 * The one place a screen asks which draft it is saving to.
 *
 * Default rather than a throw: several unit tests render a screen standalone,
 * and a missing provider means the ordinary create flow, not a crash.
 */
export const useDraftIdentity = (): IDraftIdentity => {
  const identity = useContext(DraftIdentityContext);

  return (
    identity ?? {
      mode: "create",
      isEditing: false,
      draftKey: getDraftToken(),
      screenFragment: serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN,
      screenRoute: (screenKey: string) => ({
        fragments: serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN,
        params: { screenKey },
      }),
    }
  );
};
